import type { AllAnalyticsData, AnalyticsData, FetchOptions } from './type';
import { DataCategory } from './type';

export * from './type';

const BASE_URL = import.meta.env.VITE_API_URL;

// Data sources configuration
const DATA_SOURCES = {
  overview: ['overview.json'],
  perp: [
    'perp-volume.json',
    'perp-open-interest.json',
    'perp-users.json',
    'perp-tvl.json',
    'perp-transactions.json',
    'perp-revenue.json',
    'perp-gas-consumption.json',
  ],
  rwa: [
    'rwa-volume.json',
    'rwa-users.json',
    'rwa-tvl.json',
    'rwa-transactions.json',
    'rwa-revenue.json',
    // 'rwa-gas-consumption.json',
  ],
  spot: [
    'spot-volume.json',
    'spot-users.json',
    'spot-tvl.json',
    'spot-transactions.json',
    'spot-revenue.json',
    'spot-gas-consumption.json',
  ],
} as const;

/**
 * Build complete URL for data fetching
 */
function buildUrl(filename: string): string {
  return `${BASE_URL}/latest/${filename}`;
}

/**
 * Extract metric name from filename
 * Example: 'perp-volume.json' -> 'volume'
 */
function extractMetricFromFilename(filename: string): string {
  const match = filename.match(/-([\w-]+)\.json$/);
  return match ? match[1] : filename.replace('.json', '');
}

/**
 * Convert metric name to camelCase
 * Example: 'open-interest' -> 'openInterest'
 */
function toCamelCase(str: string): string {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Fetch single data source
 */
async function fetchSingleData<T = unknown>(
  url: string,
  category: DataCategory,
  metric?: string,
  signal?: AbortSignal
): Promise<AnalyticsData<T>> {
  try {
    const response = await fetch(url, { signal });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const jsonData = await response.json();

    // Extract actual data based on response structure
    let data: T;
    if ('overview' in jsonData) {
      // Overview response
      data = jsonData.overview as T;
    } else if ('data' in jsonData) {
      // Perp/Spot response
      data = jsonData.data as T;
    } else {
      data = jsonData as T;
    }

    return {
      category,
      metric,
      data,
      timestamp: Date.now(),
      createdAt: typeof jsonData?.createdAt === 'string' ? jsonData.createdAt : undefined,
    };
  } catch (error) {
    console.error(`Failed to fetch ${url}:`, error);
    throw error;
  }
}

/**
 * Fetch all data for a specific category
 */
export async function fetchCategoryData<T = unknown>(
  category: DataCategory,
  options: FetchOptions = {}
): Promise<Record<string, AnalyticsData<T> | null>> {
  const { signal } = options;
  const filenames = DATA_SOURCES[category];

  const promises = filenames.map(async filename => {
    const url = buildUrl(filename);
    const metric =
      category === DataCategory.Overview ? undefined : extractMetricFromFilename(filename);

    try {
      return await fetchSingleData<T>(url, category, metric, signal);
    } catch (error) {
      console.error(`Failed to fetch ${category} - ${filename}:`, error);
      return null;
    }
  });

  const results = await Promise.all(promises);

  // Convert results to object
  const data: Record<string, AnalyticsData<T> | null> = {};

  if (category === DataCategory.Overview) {
    data['overview'] = results[0];
  } else {
    filenames.forEach((filename, index) => {
      const metric = extractMetricFromFilename(filename);
      const key = toCamelCase(metric);
      data[key] = results[index];
    });
  }

  return data;
}

/**
 * Fetch all analytics data (Overview + Perp + Spot)
 */
export async function fetchAllAnalyticsData(options: FetchOptions = {}): Promise<AllAnalyticsData> {
  const { signal } = options;

  try {
    // Fetch all categories concurrently
    const [overviewData, perpData, rwaData, spotData] = await Promise.all([
      fetchCategoryData(DataCategory.Overview, { signal }),
      fetchCategoryData(DataCategory.Perp, { signal }),
      fetchCategoryData(DataCategory.Rwa, { signal }),
      fetchCategoryData(DataCategory.Spot, { signal }),
    ]);

    return {
      overview: overviewData.overview as AllAnalyticsData['overview'],
      perp: {
        volume: perpData.volume as AllAnalyticsData['perp']['volume'],
        openInterest: perpData.openInterest as AllAnalyticsData['perp']['openInterest'],
        users: perpData.users as AllAnalyticsData['perp']['users'],
        tvl: perpData.tvl as AllAnalyticsData['perp']['tvl'],
        transactions: perpData.transactions as AllAnalyticsData['perp']['transactions'],
        revenue: perpData.revenue as AllAnalyticsData['perp']['revenue'],
        gasConsumption: perpData.gasConsumption as AllAnalyticsData['perp']['gasConsumption'],
      },
      rwa: {
        volume: rwaData.volume as AllAnalyticsData['rwa']['volume'],
        users: rwaData.users as AllAnalyticsData['rwa']['users'],
        tvl: rwaData.tvl as AllAnalyticsData['rwa']['tvl'],
        transactions: rwaData.transactions as AllAnalyticsData['rwa']['transactions'],
        revenue: rwaData.revenue as AllAnalyticsData['rwa']['revenue'],
        // gasConsumption: rwaData.gasConsumption as AllAnalyticsData['rwa']['gasConsumption'],
        gasConsumption: null,
      },
      spot: {
        volume: spotData.volume as AllAnalyticsData['spot']['volume'],
        users: spotData.users as AllAnalyticsData['spot']['users'],
        tvl: spotData.tvl as AllAnalyticsData['spot']['tvl'],
        transactions: spotData.transactions as AllAnalyticsData['spot']['transactions'],
        revenue: spotData.revenue as AllAnalyticsData['spot']['revenue'],
        gasConsumption: spotData.gasConsumption as AllAnalyticsData['spot']['gasConsumption'],
      },
    };
  } catch (error) {
    console.error('Failed to fetch all analytics data:', error);
    throw error;
  }
}

// Export configuration
export const config = {
  baseUrl: BASE_URL,
};
